'use client';

import { useEffect } from 'react';

import Link from 'next/link';

/**
 * Instrumentation for the qualified-lead spec page (experiment E2 of the
 * 2026-07-04 marketing research): a page-view ping fired once on mount, and a
 * tracked link used everywhere the page is referenced (pricing card, FAQ
 * answer, footer) so chat transcripts and spec-page views can be correlated
 * by entry placement.
 */

/**
 * Null-safe PostHog capture. posthog-js attaches itself to `window.posthog`
 * after init; if it is absent we no-op rather than throw (same pattern as
 * chat-widget.tsx / lib/analytics/posthog-events.ts).
 */
function captureEvent(event: string, props?: Record<string, unknown>): void {
  if (typeof window === 'undefined') return;

  const ph = (
    window as unknown as {
      posthog?: { capture?: (e: string, p?: Record<string, unknown>) => void };
    }
  ).posthog;

  ph?.capture?.(event, {
    ...props,
    source: window.location.href,
    timestamp: new Date().toISOString(),
  });
}

/** Fires the spec page-view event once when the page mounts. Renders nothing. */
export function SpecPageViewPing() {
  useEffect(() => {
    captureEvent('selenos_spec_page_viewed');
  }, []);

  return null;
}

/** Where on the site the visitor clicked through to the spec page from. */
export type SpecLinkPlacement = 'pricing' | 'faq' | 'footer';

/** A link to the spec page that records which placement was clicked. */
export function SpecLink({
  placement,
  className,
  children,
}: {
  placement: SpecLinkPlacement;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <Link
      href="/qualified-lead-spec"
      onClick={() => captureEvent('selenos_spec_link_clicked', { placement })}
      className={className}
    >
      {children}
    </Link>
  );
}
